// Reveal — restrained scroll-triggered fade + translate for editorial pages.
// Uses IntersectionObserver, one-shot. Respects prefers-reduced-motion.
import React, { useEffect, useRef, useState } from "react";

const Reveal = ({
  children,
  as: Tag = "div",
  delay = 0,
  y = 24,
  duration = 620,
  className = "",
  threshold = 0.18,
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (reduced) {
      setShown(true);
      return;
    }
    if (!ref.current) return;
    const el = ref.current;
    if (!("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, threshold]);

  const style = reduced
    ? {}
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? "translate3d(0,0,0)" : `translate3d(0, ${y}px, 0)`,
        transition: `opacity ${duration}ms cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}ms`,
        willChange: "opacity, transform",
      };

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
